import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { api } from '../api/client';
import { errText, Modal, useMeta, useToast } from './ui';
import { partOf } from '../lib/format';

/* eslint-disable @typescript-eslint/no-explicit-any */
/** "2 moved: 1 to Scott Fullarton, 1 unscheduled" */
export const movedText = (m: { operations: number; to: { name: string; operations: number }[]; unscheduled: number }) =>
  `${m.operations} moved: ${[...m.to.map((t) => `${t.operations} to ${t.name}`), ...(m.unscheduled ? [`${m.unscheduled} unscheduled`] : [])].join(', ')}`;
const REASONS: { id: string; label: string }[] = [{ id: 'sick', label: 'Sick' }, { id: 'holiday', label: 'Holiday' }, { id: 'unauthorised', label: 'Unauthorised' }];

/** For line managers, on every page: people with no work started and nothing recorded, and people back from sickness waiting for their return to work. */
export function AttendanceBar() {
  const qc = useQueryClient();
  const toast = useToast();
  // In the company's time zone: the server reads the time entered in it
  const zone = useMeta().data?.timezone ?? 'Europe/London';
  const t = (iso: string) => DateTime.fromISO(iso).setZone(zone).toFormat('HH:mm');
  const d = (iso: string) => DateTime.fromISO(iso).setZone(zone).toFormat('ccc d LLL');
  const q = useQuery({ queryKey: ['attendance'], queryFn: () => api.get<{ notIn: any[]; rtw: any[] }>('/api/hr/attendance'), refetchInterval: 120_000 });
  const [letting, setLetting] = useState<any | null>(null);
  const [why, setWhy] = useState('');
  const done = () => ['attendance', 'hr-file', 'hr-files', 'hr-rtw', 'board', 'exceptions'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const [times, setTimes] = useState<Record<string, string>>({});
  const [until, setUntil] = useState<Record<string, string>>({});
  const record = useMutation({
    mutationFn: ({ id, reason, at, until }: { id: string; reason: string; at?: string; until?: string }) => api.post<any>(`/api/hr/attendance/${id}/reason`, { reason, at, until }),
    onSuccess: (r) => {
      done();
      if (r?.resumed) toast('ok', `${partOf(r.resumed)} ${r.resumed.operation} resumed`);
      if (r?.moved?.operations) toast('ok', movedText(r.moved));
    },
    onError: (e) => toast('bad', errText(e)),
  });
  const today = DateTime.now().setZone(zone).toISODate()!;
  const letStart = useMutation({
    mutationFn: () => api.post(`/api/hr/attendance/${letting.resourceId}/let-start`, { reason: why }),
    onSuccess: () => { done(); setLetting(null); setWhy(''); }, onError: (e) => toast('bad', errText(e)),
  });
  const [offUntil, setOffUntil] = useState<Record<string, string>>({});
  const still = useMutation({
    mutationFn: ({ id, until }: { id: string; until?: string }) => api.post<any>(`/api/hr/attendance/${id}/still-off`, { until }),
    onSuccess: (r) => { done(); if (r?.moved?.operations) toast('ok', movedText(r.moved)); },
    onError: (e) => toast('bad', errText(e)),
  });
  const notIn = q.data?.notIn ?? [], rtw = q.data?.rtw ?? [];
  if (!notIn.length && !rtw.length) return null;
  return (
    <div className="att-bar" data-testid="attendance-bar">
      {notIn.map((p) => (
        <div key={p.resourceId} className="att-row" data-testid={`not-in-${p.name}`}>
          <span className="att-tag bad">No work started</span>
          <b>{p.name}</b><span className="faint">due {t(p.start)}{p.carried ? ` · ${partOf(p.carried)} ${p.carried.operation}` : ''}{p.escalated && p.lineManager ? ` · ${p.lineManager}'s team` : ''}</span>
          {p.canChange && <span className="att-acts">
            {REASONS.map((r) => <button key={r.id} className="btn sm" disabled={record.isPending} onClick={() => record.mutate({ id: p.resourceId, reason: r.id, until: until[p.resourceId] || undefined })}>{r.label}</button>)}
            <label className="att-until">until<input className="input sm" type="date" min={today} aria-label={`${p.name} off until`} value={until[p.resourceId] ?? ''} onChange={(e) => setUntil({ ...until, [p.resourceId]: e.target.value })} /></label>
            <span className="att-at">
              <button className="btn sm" disabled={record.isPending} onClick={() => record.mutate({ id: p.resourceId, reason: 'resumed', at: times[p.resourceId] ?? t(p.start) })}>{p.carried ? 'Resumed at' : 'In at'}</button>
              <input className="input sm" type="time" aria-label={`${p.name} ${p.carried ? 'resumed' : 'in'} at`} value={times[p.resourceId] ?? t(p.start)} onChange={(e) => setTimes({ ...times, [p.resourceId]: e.target.value })} />
            </span>
          </span>}
        </div>
      ))}
      {rtw.map((p) => (
        <div key={p.resourceId} className="att-row" data-testid={`rtw-needed-${p.name}`}>
          <span className="att-tag warn">Return to work</span>
          <b>{p.name}</b><span className="faint">back {d(p.returnDate)} · sick {d(p.spellFrom)}{p.spellTo !== p.spellFrom ? ` to ${d(p.spellTo)}` : ''}</span>
          <span className="att-acts">
            <Link className="btn sm primary" to={`/hr/files/${p.resourceId}?rtw=${p.returnDate}`}>Return to work</Link>
            {p.canChange && <>
              <button className="btn sm" disabled={still.isPending} onClick={() => still.mutate({ id: p.resourceId, until: offUntil[p.resourceId] || undefined })}>Still off</button>
              <label className="att-until">until<input className="input sm" type="date" min={today} aria-label={`${p.name} still off until`} value={offUntil[p.resourceId] ?? ''} onChange={(e) => setOffUntil({ ...offUntil, [p.resourceId]: e.target.value })} /></label>
              <button className="btn sm" onClick={() => setLetting(p)}>Let start</button>
            </>}
          </span>
        </div>
      ))}
      {letting && (
        <Modal title={`Let ${letting.name} start`} onClose={() => setLetting(null)} footer={<><button className="btn" onClick={() => setLetting(null)}>Cancel</button><button className="btn primary" disabled={!why.trim() || letStart.isPending} onClick={() => letStart.mutate()}>Let start</button></>}>
          <label className="field">Reason<input className="input" value={why} onChange={(e) => setWhy(e.target.value)} autoFocus maxLength={500} /></label>
        </Modal>
      )}
    </div>
  );
}
